/**
 * Every URL of the new brand campaign flow (brd_* tables), in one place.
 * Screens navigate with these builders — never hand-written paths — so a
 * route can move without hunting for strings. Full map: docs/brand-campaign-flow/URL_MAP.md.
 *
 *   /brand/home                           Home (app Home + web Overview / Campaign Insights)
 *   /brand/notifications                  Notifications (the bell)
 *   /brand/discover                       Discover Creators              ?platform=youtube · ?list=<listId> (pick for a list) · ?category=<id> · ?country=&state=&city= · ?q=<text>
 *   /brand/creator                        A creator's Instagram Profile Insights   ?sm_id=&influencerId= · ?asked=1 (Discover asked first)
 *   /brand/campaigns                      Campaigns (My Briefs)          ?tab=tracking → Tracking
 *   /brand/campaigns/new                  Create Brief                    ?returnTo=<path>
 *   /brand/campaigns/:campaignId          Campaign detail                 ?focus=analytics
 *   /brand/campaigns/:campaignId/edit     Edit a Draft / Rejected brief
 *   /brand/track                          Track Campaign (pasted post links)
 *   /brand/inbox                          Inbox: All Messages            ?view=briefs · ?brief=<campaignId>
 *   /brand/inbox/:campaignId/:influencerId  A conversation (campaignId 0 = direct chat)
 *   /brand/profile[/…]                    Profile and the screens it opens (lists, activity,
 *                                         help, subscription, personal-info, company-details,
 *                                         notifications, subscription-history, security[/deactivate|/delete])
 *   /brand/profile/review-campaigns       Admin → Review Campaigns       ?tab=reviewed · ?q= · ?type= · ?outcome=
 *   /brand/profile/review-campaigns/:campaignId[/edit]   One brief to approve / reject; edit it
 *
 * routes/route.tsx mounts the flow as the /brand/* catch-all, so every other
 * /brand URL (settings, plans, profile-insight …) stays the older page's.
 * Two older pages had the URLs the flow now uses and moved:
 *   - the web's old Discover → /brand/old-discover (the old Lists page opens it to add creators);
 *   - the legacy campaign view → /brand/old-campaigns/* (proposals, payment, creative, analytics,
 *     campaign-details, add-influencers). Its old URLs (/brand/campaigns/proposals?planID=&campaignID= …,
 *     in emails and invoices already sent) redirect there — see OLD_CAMPAIGN_TABS.
 * The old profile pages stay at /brand/profile-insight (Instagram, also an
 * influencer's own) and /brand/profile-insight-yt (YouTube — the new flow still
 * opens it). The rest of the legacy campaign flow keeps its URLs (/brand/plans,
 * /brand/campaign/*, /brand/campaign-msg, /brand/direct-msg).
 * The flow's first URLs (/brand/brd/…) redirect to the same page here.
 * None of the old campaign / list / inbox pages is linked from the UI any more
 * (Oct 25); only their URLs keep working, for emails and invoices already sent.
 */

export const BRD_BASE = '/brand';

/** The flow's top-level pages (BrandFlowRoutes) — everything else under /brand is an older page. */
const FLOW_SECTIONS = ['home', 'notifications', 'discover', 'creator', 'campaigns', 'track', 'inbox', 'profile'];

/** The legacy campaign view's tabs: /brand/campaigns/<tab> redirects to /brand/old-campaigns/<tab> (routes/route.tsx). */
export const OLD_CAMPAIGN_TABS = ['proposals', 'payment', 'creative', 'analytics', 'campaign-details', 'add-influencers'];

/** Whether a path is in the new flow (App.tsx frames these pages before the flow's code loads). */
export const isBrdFlowPath = (pathname: string) => {
  // Route matching ignores case, so this does too.
  const [, root, section, sub] = pathname.toLowerCase().split('/');
  if (root !== 'brand' || !FLOW_SECTIONS.includes(section)) return false;
  return !(section === 'campaigns' && OLD_CAMPAIGN_TABS.includes(sub));
};

/**
 * Older /brand pages restyled in the flow's look (they keep their own URLs and
 * code): App.tsx gives them the flow's page frame too (brdRouteShell.scss).
 */
const FRAMED_OLD_PAGES = ['storefront'];

/** Whether a path gets the new flow's page frame: a flow page or a restyled older one. */
export const isBrdFramedPath = (pathname: string) => {
  const [, root, section] = pathname.toLowerCase().split('/');
  return isBrdFlowPath(pathname) || (root === 'brand' && FRAMED_OLD_PAGES.includes(section));
};

/**
 * Older pages restyled in the flow's look that keep the site header on phones
 * (they have no tab bar of their own): App.tsx gives them only the flow's
 * background and top spacing (brdRouteShell.scss, html.brd-look-route).
 */
const LOOK_ONLY_PAGES = ['/brand/info/details'];
export const isBrdLookPath = (pathname: string) => LOOK_ONLY_PAGES.includes(pathname.toLowerCase().replace(/\/+$/, ''));

/** The admin dashboard (/brand/admin-dashboard/*): App.tsx themes it in the flow's look (admin-theme.scss). */
export const isBrdAdminPath = (pathname: string) => /^\/(brand|agency)\/admin-dashboard(\/|$)/i.test(pathname);

const query = (params: Record<string, string | number | null | undefined>) => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 0)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return qs ? `?${qs}` : '';
};

export const brdPaths = {
  /**
   * Discover Creators; `list`: pick creators for that list (List → Add creators);
   * `category` / `q`: a Home category or city tile (a fresh search with just that).
   */
  discover: (opts?: {
    platform?: 'instagram' | 'youtube'; list?: number | string; category?: number | string; q?: string;
    /** A Home city tile: its Location filter. */
    location?: { countryId: number; stateId: number; cityIds: number[] };
  }) =>
    `${BRD_BASE}/discover${query({
      platform: opts?.platform === 'youtube' ? 'youtube' : undefined,
      list: opts?.list,
      category: opts?.category,
      q: opts?.q,
      country: opts?.location?.countryId,
      state: opts?.location?.stateId,
      city: opts?.location?.cityIds.join(','),
    })}`,
  /** Home: the app's Home tab, plus the web's Overview and Campaign Insights. */
  home: () => `${BRD_BASE}/home`,
  /** The brand's notification center (Home's bell); `notifications` is the settings page. */
  notificationCenter: () => `${BRD_BASE}/notifications`,
  /** A creator's Instagram Profile Insights; `asked`: Discover already asked before using a free report. */
  creator: (smId: number | string, influencerId?: number | string | null, opts?: { asked?: boolean }) =>
    `${BRD_BASE}/creator${query({ sm_id: smId, influencerId: influencerId ?? undefined, asked: opts?.asked ? 1 : undefined })}`,
  campaigns: (tab?: 'my-briefs' | 'tracking') => `${BRD_BASE}/campaigns${tab === 'tracking' ? '?tab=tracking' : ''}`,
  newBrief: (returnTo?: string) => `${BRD_BASE}/campaigns/new${query({ returnTo })}`,
  campaign: (campaignId: number | string, opts?: { analytics?: boolean }) =>
    `${BRD_BASE}/campaigns/${campaignId}${opts?.analytics ? '?focus=analytics' : ''}`,
  editBrief: (campaignId: number | string) => `${BRD_BASE}/campaigns/${campaignId}/edit`,
  track: () => `${BRD_BASE}/track`,
  inbox: (opts?: { view?: 'all' | 'briefs'; brief?: number | string | null }) =>
    `${BRD_BASE}/inbox${query({ view: opts?.view === 'briefs' && !opts?.brief ? 'briefs' : undefined, brief: opts?.brief ?? undefined })}`,
  chat: (campaignId: number | string | null | undefined, influencerId: number | string, opts?: { brief?: number | string | null }) =>
    `${BRD_BASE}/inbox/${Number(campaignId) || 0}/${influencerId}${query({ brief: opts?.brief ?? undefined })}`,
  /** The app's Profile tab and the screens it opens. */
  profile: () => `${BRD_BASE}/profile`,
  lists: () => `${BRD_BASE}/profile/lists`,
  list: (listId: number | string) => `${BRD_BASE}/profile/lists/${listId}`,
  activity: () => `${BRD_BASE}/profile/activity`,
  help: () => `${BRD_BASE}/profile/help`,
  subscription: () => `${BRD_BASE}/profile/subscription`,
  /** The app's settings screens (a brand, and an agency inside one of its brands — as the app). */
  personalInfo: () => `${BRD_BASE}/profile/personal-info`,
  companyDetails: () => `${BRD_BASE}/profile/company-details`,
  notifications: () => `${BRD_BASE}/profile/notifications`,
  subscriptionHistory: () => `${BRD_BASE}/profile/subscription-history`,
  /** An agency: every brand's plan (Go Premium / history per brand). */
  brandSubscriptions: () => `${BRD_BASE}/profile/brand-subscriptions`,
  security: () => `${BRD_BASE}/profile/security`,
  deactivateAccount: () => `${BRD_BASE}/profile/security/deactivate`,
  deleteAccount: () => `${BRD_BASE}/profile/security/delete`,
  /** Admin → Review Campaigns (ClanConnect admins only; the API checks every call). */
  reviewCampaigns: () => `${BRD_BASE}/profile/review-campaigns`,
  reviewCampaign: (campaignId: number | string) => `${BRD_BASE}/profile/review-campaigns/${campaignId}`,
  reviewCampaignEdit: (campaignId: number | string) => `${BRD_BASE}/profile/review-campaigns/${campaignId}/edit`,
};

/**
 * Where each Profile row goes: the app's settings screens (new URLs above), for
 * a brand and for an agency inside one of its brands alike — as in the app.
 * An agency also gets Brand Subscriptions (every brand's plan). Subscription
 * needs a picked brand (without one: the brand switcher); Subscription History
 * without one lists every brand's purchases.
 * (OLD: an agency kept its own settings pages under /agency/settings.)
 */
export const profileLinks = (accountType: 'Brand' | 'Agency', brandId?: unknown) => {
  const agency = accountType === 'Agency';
  const brandPicked = !agency || Number(brandId) > 0;
  return {
    personalInfo: brdPaths.personalInfo(),
    companyDetails: brdPaths.companyDetails(),
    notifications: brdPaths.notifications(),
    subscription: brandPicked ? brdPaths.subscription() : '/switch/user',
    subscriptionHistory: brdPaths.subscriptionHistory(),
    brandSubscriptions: brdPaths.brandSubscriptions(),
    security: brdPaths.security(),
    zapAutomations: '/brand/zap/automations',
    adminReview: brdPaths.reviewCampaigns(),
    switchBrand: '/switch/user',
    switchAccount: '/switch/account',
    discover: brdPaths.discover(),
  };
};

/** Premium plans (brand settings). */
export const BRAND_SUBSCRIPTION_PATH = `${BRD_BASE}/profile/subscription`;

/**
 * A creator's profile: Instagram → the new Profile Insights; YouTube → the
 * web's YouTube profile page (not rebuilt yet).
 */
export const creatorProfilePath = (
  platform: string | null | undefined,
  smId: number | string,
  influencerId?: number | string | null,
  opts?: { asked?: boolean },
) =>
  String(platform || '').toLowerCase() === 'youtube'
    ? `/brand/profile-insight-yt?sm_id=${smId}${influencerId ? `&influencerId=${influencerId}` : ''}`
    : brdPaths.creator(smId, influencerId, opts);
